import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Search, Trash2, Sparkles, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import LeadDrawer from "@/components/LeadDrawer";
import CSVImportDialog from "@/components/CSVImportDialog";
import { toast } from "sonner";

const SEGMENTS = ["Swiss Broker", "Real Estate Agent", "Family Office", "Entrepreneur", "Business Owner"];
const STAGES = ["New", "Contacted", "Qualified", "Meeting", "Proposal", "Won", "Lost"];

const stageColor = (s) => ({
  New: "bg-slate-100 text-slate-700",
  Contacted: "bg-blue-50 text-blue-700",
  Qualified: "bg-amber-50 text-amber-800",
  Meeting: "bg-[#FDF8EF] text-[#8A6B33]",
  Proposal: "bg-purple-50 text-purple-700",
  Won: "bg-emerald-50 text-emerald-700",
  Lost: "bg-red-50 text-red-700",
}[s] || "bg-slate-100 text-slate-700");

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", segment: "Family Office", location: "Switzerland", budget_sgd: "", net_worth_range: "", stage: "New", notes: "", source: "Manual" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (segmentFilter !== "all") params.segment = segmentFilter;
      if (stageFilter !== "all") params.stage = stageFilter;
      if (q) params.q = q;
      const res = await api.get("/leads", { params });
      setLeads(res.data);
    } catch (_e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [segmentFilter, stageFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const submitNew = async () => {
    if (!form.name || !form.segment) { toast.error("Name and segment required"); return; }
    try {
      const payload = { ...form, budget_sgd: form.budget_sgd ? Number(form.budget_sgd) : null };
      await api.post("/leads", payload);
      toast.success("Lead added");
      setOpenNew(false);
      setForm({ name: "", email: "", phone: "", company: "", segment: "Family Office", location: "Switzerland", budget_sgd: "", net_worth_range: "", stage: "New", notes: "", source: "Manual" });
      load();
    } catch (_e) { toast.error("Failed to add lead"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead removed");
    load();
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">CRM</div>
          <h1 className="font-serif-display text-4xl text-[#0A1128] mt-2 tracking-tight">Leads</h1>
          <p className="text-slate-500 mt-2 text-sm">{leads.length} contact{leads.length === 1 ? "" : "s"} across the Swiss network.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="import-csv-btn" variant="outline" onClick={() => setOpenImport(true)} className="border-[#E2E8F0] rounded-md">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button data-testid="new-lead-btn" onClick={() => setOpenNew(true)} className="bg-[#0A1128] text-white hover:bg-[#152243] rounded-md">
            <Plus className="w-4 h-4 mr-2" /> New lead
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" className="pl-9 border-[#E2E8F0]" />
        </div>
        <Select value={segmentFilter} onValueChange={setSegmentFilter}>
          <SelectTrigger data-testid="filter-segment" className="w-52 border-[#E2E8F0]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All segments</SelectItem>
            {SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger data-testid="filter-stage" className="w-44 border-[#E2E8F0]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Name</TableHead>
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Segment</TableHead>
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Company</TableHead>
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Location</TableHead>
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Budget (SGD)</TableHead>
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Stage</TableHead>
              <TableHead className="text-[11px] uppercase tracking-widest text-slate-500 w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="space-y-1"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-40" /></div></TableCell>
                  <TableCell><Skeleton className="h-5 w-24 rounded-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                </TableRow>
              ))
            ) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">No leads yet. Add one or seed demo data from Overview.</TableCell></TableRow>
            ) : (
              leads.map((l) => (
              <TableRow key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} onClick={() => setSelected(l)} className="cursor-pointer hover:bg-[#FDF8EF]/40">
                <TableCell>
                  <div className="font-medium text-[#0A1128]">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.email || "—"}</div>
                </TableCell>
                <TableCell><Badge variant="secondary" className="bg-slate-100 text-slate-800 rounded-full">{l.segment}</Badge></TableCell>
                <TableCell className="text-sm text-slate-700">{l.company || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{l.location || "—"}</TableCell>
                <TableCell className="text-sm">{l.budget_sgd ? new Intl.NumberFormat("en-SG").format(l.budget_sgd) : "—"}</TableCell>
                <TableCell><span className={`text-xs px-2.5 py-1 rounded-full ${stageColor(l.stage)}`}>{l.stage}</span></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <Button data-testid={`ai-draft-${l.lead_id}`} size="icon" variant="ghost" onClick={() => setSelected({ ...l, _openAI: true })} title="AI draft email">
                      <Sparkles className="w-4 h-4 text-[#C5A880]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(l.lead_id)} title="Delete">
                      <Trash2 className="w-4 h-4 text-slate-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </motion.div>

      {/* New lead dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="new-lead-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Add a lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><Label>Name*</Label><Input data-testid="new-lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="new-lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label>Segment*</Label>
              <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                <SelectTrigger data-testid="new-lead-segment"><SelectValue /></SelectTrigger>
                <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div><Label>Budget SGD</Label><Input type="number" value={form.budget_sgd} onChange={(e) => setForm({ ...form, budget_sgd: e.target.value })} /></div>
            <div><Label>Net worth range</Label><Input value={form.net_worth_range} onChange={(e) => setForm({ ...form, net_worth_range: e.target.value })} placeholder="e.g. CHF 20M-50M" /></div>
            <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button data-testid="new-lead-submit" onClick={submitNew} className="bg-[#0A1128] text-white hover:bg-[#152243]">Add lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadDrawer lead={selected} onClose={() => setSelected(null)} onChange={load} />
      <CSVImportDialog open={openImport} onClose={() => setOpenImport(false)} onImported={load} />
    </div>
  );
}
